import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  downloadPerformaPaymentReceipt,
  getPerformaInvoiceHistory
} from '../services/api/implementation/impl';
import Loader from '../../shared/components/ui/Loader';
import PaginationComponent from '../../shared/components/PaginationComponent';
import {
  EmptyRow,
  Field,
  FilterBar,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead
} from '../../shared/components/ui/primitives';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { money, shortDate } from './SiteWallet/siteWalletUi';
import { filterInput } from './PerformaInvoiceList';
import { FileLinks, PdfButton, PI_PAYMENT_MODES } from './PerformaInvoiceParts';

const MODE_LABEL = Object.fromEntries(PI_PAYMENT_MODES.map((m) => [m.value, m.label]));

const PAGE_SIZE = 20;

const EMPTY = { search: '', project_id: '', from: '', to: '' };

/** Every payment recorded against a Proforma Invoice, one row per payment. */
const PerformaInvoiceHistory = () => {
  const { projects = [] } = useProjectsList();
  const [filters, setFilters] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: search.trim() })), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(1), [filters]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filtered = search || Object.entries(filters).some(([k, v]) => k !== 'search' && v);

  const history = useQuery({
    queryKey: ['performaInvoiceHistory', page, filters],
    queryFn: () => getPerformaInvoiceHistory(page, PAGE_SIZE, filters),
    keepPreviousData: true
  });

  const rows = history.data?.data ?? [];
  const meta = history.data?.meta ?? {};
  const totalPages = meta.total ? Math.ceil(meta.total / PAGE_SIZE) : 1;

  return (
    <div className="mt-[9.5rem] flex flex-col gap-4 px-3 pb-6">
      <Loader isVisible={history.isFetching && !history.data} />

      <FilterBar
        end={
          <>
            <Field label="Paid from" size="caption">
              <input
                type="date"
                value={filters.from}
                onChange={set('from')}
                className={filterInput}
              />
            </Field>
            <Field label="To" size="caption">
              <input type="date" value={filters.to} onChange={set('to')} className={filterInput} />
            </Field>
            {filtered && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilters(EMPTY);
                }}
                className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
              >
                Clear
              </button>
            )}
          </>
        }
      >
        <Field label="Search" size="caption">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Vendor, PI or PO number"
            className={`${filterInput} w-56`}
          />
        </Field>
        <Field label="Project" size="caption">
          <select value={filters.project_id} onChange={set('project_id')} className={filterInput}>
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      </FilterBar>

      <TableCard>
        <Table>
          <THead>
            <tr>
              <Th>Paid on</Th>
              <Th>PI no.</Th>
              <Th>Vendor</Th>
              <Th>Project</Th>
              <Th>Mode</Th>
              <Th>UTR / reference</Th>
              <Th>Recorded by</Th>
              <Th align="right">Amount</Th>
              <Th align="right">Receipt</Th>
            </tr>
          </THead>
          <TBody>
            {!rows.length && !history.isFetching && (
              <EmptyRow colSpan={9}>
                No payments{filtered ? ' for these filters' : ' yet'}.
              </EmptyRow>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <Td className="whitespace-nowrap text-gray-600">
                  {shortDate(r.payment_date ?? r.created_at)}
                </Td>
                <Td className="whitespace-nowrap font-medium">{r.pi_number ?? `PI-${r.pi_id}`}</Td>
                <Td>{r.vendor_name ?? '—'}</Td>
                <Td className="text-gray-600">{r.project?.name ?? '—'}</Td>
                <Td className="text-gray-600">{r.mode ? (MODE_LABEL[r.mode] ?? r.mode) : '—'}</Td>
                <Td className="text-gray-600">
                  {r.reference_no || '—'}
                  {r.proof_urls?.length > 0 && (
                    <div className="mt-1">
                      <FileLinks urls={r.proof_urls} label="Proof" />
                    </div>
                  )}
                </Td>
                <Td className="text-gray-600">{r.recorded_by ?? '—'}</Td>
                <Td align="right" numeric className="font-medium">
                  {money(r.paidAmount)}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <PdfButton
                    compact
                    label="Receipt"
                    onDownload={() => downloadPerformaPaymentReceipt(r.id)}
                  />
                </Td>
              </tr>
            ))}
          </TBody>
        </Table>
      </TableCard>

      {meta.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-gray-500">
            {meta.total.toLocaleString('en-IN')} payment{meta.total === 1 ? '' : 's'} ·{' '}
            {money(meta.total_amount ?? 0)} {filtered ? 'for these filters' : 'in all'}
          </span>
          {totalPages > 1 && (
            <div className="flex-1">
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                isFetching={history.isFetching}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PerformaInvoiceHistory;
